import React, { useContext, useEffect, useState } from "react";
import {
  Alert,
  CircularProgress,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Button,
  IconButton,
  Collapse,
  Box,
} from "@mui/material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import { getFormattedDate } from "../utils/utils";
import { convertCurrencyFormat } from "../helpers";
import { collection, getDocs, orderBy, query, where } from "firebase/firestore";
import { db } from "../firebase-config";
import { UserContext } from "../contexts/userContext";
import { ISaleList } from "../interfaces";
import { BUSINESS_COLLECTION, SALES_COLLECTION } from "../utils/constants";

const SalesList: React.FC = () => {
  const { businessData } = useContext(UserContext);
  const [loading, setLoading] = useState(false);
  const [sales, setSales] = useState<ISaleList[]>([]);
  const [page, setPage] = useState(0);
  const [totalItems, setTotalItems] = useState(0);
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [searchResults, setSearchResults] = useState<ISaleList[] | null>(null);
  const pageSize = 10;

  // Load all completed sales (ordered by createdAt desc). We'll paginate client-side for simplicity.
  useEffect(() => {
    if (!businessData?.uid) return;
    const salesRef = collection(
      db,
      BUSINESS_COLLECTION,
      businessData.uid,
      SALES_COLLECTION
    );
    const fetchAll = async () => {
      setLoading(true);
      try {
        const q = query(
          salesRef,
          where("status", "==", "completed"),
          orderBy("createdAt", "desc")
        );
        const snap = await getDocs(q);
        const items = snap.docs.map(
          (d) => ({ id: d.id, ...d.data() } as ISaleList)
        );
        setSales(items);
        setTotalItems(items.length);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    fetchAll();
  }, [businessData]);

  const handleChangePage = (_: any, newPage: number) => setPage(newPage);

  // Non-paginated date search (returns all matches)
  const handleDateSearch = async () => {
    if (!businessData?.uid) return;
    setLoading(true);
    try {
      const salesRef = collection(
        db,
        BUSINESS_COLLECTION,
        businessData.uid,
        SALES_COLLECTION
      );
      const constraints: any[] = [where("status", "==", "completed")];
      if (dateFrom)
        constraints.push(
          where("createdAt", ">=", new Date(dateFrom + "T00:00:00"))
        );
      if (dateTo)
        constraints.push(
          where("createdAt", "<=", new Date(dateTo + "T23:59:59.999"))
        );
      const q = constraints.length
        ? query(salesRef, ...constraints, orderBy("createdAt", "desc"))
        : query(salesRef, orderBy("createdAt", "desc"));
      const snap = await getDocs(q);
      const items = snap.docs.map(
        (d) => ({ id: d.id, ...d.data() } as ISaleList)
      );
      setSearchResults(items);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const [openRows, setOpenRows] = useState<Record<string, boolean>>({});

  const toggleRow = (id: string) => {
    setOpenRows((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const displayed =
    searchResults !== null
      ? searchResults
      : sales.slice(page * pageSize, page * pageSize + pageSize);

  return (
    <Grid container spacing={3} p={3} mt={10} sx={{ width: "100%" }}>
      <Grid size={12} display="flex" gap={2} alignItems="center">
        <TextField
          type="date"
          label="Desde"
          InputLabelProps={{ shrink: true }}
          value={dateFrom}
          onChange={(e) => setDateFrom(e.target.value)}
          size="small"
        />
        <TextField
          type="date"
          label="Hasta"
          InputLabelProps={{ shrink: true }}
          value={dateTo}
          onChange={(e) => setDateTo(e.target.value)}
          size="small"
        />
        <Button variant="contained" size="small" onClick={handleDateSearch}>
          Buscar
        </Button>
      </Grid>

      <Grid size={12}>
        {loading ? (
          <Grid container justifyContent="center">
            <CircularProgress size={60} sx={{ mt: 4 }} />
          </Grid>
        ) : (
          <>
            {displayed.length > 0 ? (
              <TableContainer component={Paper}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell />
                      <TableCell>Fecha</TableCell>
                      <TableCell>ID venta</TableCell>
                      <TableCell>Monto total</TableCell>
                      <TableCell>Estado</TableCell>
                      <TableCell>Productos</TableCell>
                      <TableCell>Promociones</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {displayed.map((s: any, idx: number) => {
                      const id = s.id ?? String(idx);
                      return (
                        <React.Fragment key={id}>
                          <TableRow>
                            <TableCell>
                              <IconButton
                                aria-label="expand row"
                                size="small"
                                onClick={() => toggleRow(id)}
                              >
                                {openRows[id] ? (
                                  <KeyboardArrowUpIcon />
                                ) : (
                                  <KeyboardArrowDownIcon />
                                )}
                              </IconButton>
                            </TableCell>
                            <TableCell>
                              {getFormattedDate(
                                s.createdAt ?? s.startDate ?? ""
                              )}
                            </TableCell>
                            <TableCell>{s.id}</TableCell>
                            <TableCell>
                              {convertCurrencyFormat(
                                s.total ?? s.totalAmount ?? 0
                              )}
                            </TableCell>
                            <TableCell>{s.status}</TableCell>
                            <TableCell>
                              {Array.isArray(s.products)
                                ? s.products.length
                                : 0}
                            </TableCell>
                            <TableCell>
                              {Array.isArray(s.promotion)
                                ? s.promotion.length
                                : 0}
                            </TableCell>
                          </TableRow>

                          <TableRow>
                            <TableCell
                              style={{ paddingBottom: 0, paddingTop: 0 }}
                              colSpan={7}
                            >
                              <Collapse
                                in={!!openRows[id]}
                                timeout="auto"
                                unmountOnExit
                              >
                                <Box margin={1}>
                                  <Table size="small" aria-label="order-items">
                                    <TableHead>
                                      <TableRow>
                                        <TableCell>Producto</TableCell>
                                        <TableCell>Cantidad</TableCell>
                                        <TableCell>Precio unit.</TableCell>
                                        <TableCell>Precio total</TableCell>
                                      </TableRow>
                                    </TableHead>
                                    <TableBody>
                                      {Array.isArray(s.products) &&
                                      s.products.length > 0 ? (
                                        s.products.map((p: any, i: number) => {
                                          const unitPrice = p.price ?? 0;
                                          const qty = p.quantityToSale ?? 0;
                                          const total = unitPrice * qty;
                                          return (
                                            <>
                                              <TableRow key={p.id ?? i}>
                                                <TableCell>{p.name}</TableCell>
                                                <TableCell>{qty}</TableCell>
                                                <TableCell>
                                                  {convertCurrencyFormat(
                                                    unitPrice
                                                  )}
                                                </TableCell>
                                                <TableCell>
                                                  {convertCurrencyFormat(total)}
                                                </TableCell>
                                                {/* <TableCell>
                                                  {p.promotions &&
                                                  p.promotions.length > 0
                                                    ? p.promotions
                                                        .map(
                                                          (pr: any) => pr.name
                                                        )
                                                        .join(", ")
                                                    : "-"}
                                                </TableCell> */}
                                              </TableRow>
                                            </>
                                          );
                                        })
                                      ) : (
                                        <TableRow>
                                          <TableCell colSpan={5}>
                                            No hay productos
                                          </TableCell>
                                        </TableRow>
                                      )}

                                      {/* Mostrar promociones vinculadas a la venta (promotions applied as bundles) */}
                                      {Array.isArray(s.promotion) &&
                                        s.promotion.length > 0 && (
                                          <>
                                            <TableRow>
                                              <TableCell
                                                colSpan={5}
                                                sx={{ fontWeight: "bold" }}
                                              >
                                                Promociones aplicadas en la
                                                venta:
                                              </TableCell>
                                            </TableRow>
                                            {s.promotion.map(
                                              (pr: any, pi: number) => {
                                                return (
                                                  <TableRow key={pi}>
                                                    <TableCell>
                                                      {pr.promotion?.name ??
                                                        pr.name ??
                                                        "Promoción"}
                                                    </TableCell>
                                                    <TableCell>
                                                      {pr.quantity ?? "-"}
                                                    </TableCell>
                                                    <TableCell>
                                                      {convertCurrencyFormat(
                                                        pr.promotion.value
                                                      )}
                                                    </TableCell>
                                                    <TableCell>
                                                      {convertCurrencyFormat(
                                                        pr.promotion.value *
                                                          pr.quantity
                                                      )}
                                                    </TableCell>
                                                  </TableRow>
                                                );
                                              }
                                            )}
                                          </>
                                        )}
                                    </TableBody>
                                  </Table>
                                </Box>
                              </Collapse>
                            </TableCell>
                          </TableRow>
                        </React.Fragment>
                      );
                    })}
                  </TableBody>
                </Table>
                {searchResults === null && (
                  <TablePagination
                    rowsPerPageOptions={[pageSize]}
                    rowsPerPage={pageSize}
                    component="div"
                    page={page}
                    count={totalItems}
                    onPageChange={handleChangePage}
                  />
                )}
              </TableContainer>
            ) : (
              <Alert severity="warning">No hay ventas cargadas.</Alert>
            )}
          </>
        )}
      </Grid>
    </Grid>
  );
};

export default SalesList;
